import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Space, Table, Tag, Modal, message, Spin } from 'antd';
import type { Invitation } from '../../types/domain';
import { formatDateTime } from '../../utils/format';
import { useInvitationsData } from '../../hooks/data/useInvitationsData';

const InviteListTable = (): ReactNode => {
  const { t } = useTranslation();
  const { isLoading, invitations, deleteInvitation } = useInvitationsData();

  const handleClickDeleteInvitation = (invitation: Invitation): void => {
    Modal.confirm({
      title: t('message.confirmCancelInvitation', {
        email: invitation.invitee.email,
      }),
      cancelText: t('cancel'),
      okText: t('actions.cancel'),
      okType: 'danger',
      onOk: (): void => {
        const messageKey = 'delete-invitation-message';
        message.loading({
          key: messageKey,
          content: t('message.cancelingInvitation'),
          duration: 0,
        });

        deleteInvitation(invitation.id)
          .then((): void => {
            message.success({
              key: messageKey,
              content: t('message.invitationCancelled'),
              duration: 4,
            });
          })
          .catch((): void => {
            message.error({
              key: messageKey,
              content: t('message.failedToCancelInvitation'),
              duration: 4,
            });
          });
      },
      centered: true,
      maskClosable: true,
    });
  };

  return (
    <Spin size="large" spinning={isLoading}>
      <Table
        dataSource={invitations}
        pagination={{
          pageSize: 10,
        }}
        rowKey="id"
        columns={[
          {
            title: t('emailAddress'),
            dataIndex: ['invitee', 'email'],
          },
          {
            title: t('role'),
            render: (_: unknown, record: Invitation) => {
              switch (record.is_admin) {
                case true:
                  return <Tag color="pink">{t('roleAdmin')}</Tag>;
                case false:
                  return <Tag color="blue">{t('roleMember')}</Tag>;
              }
            },
          },
          {
            title: t('invitedDatetime'),
            render: (_: unknown, record: Invitation): ReactNode => {
              return formatDateTime(record.created_at);
            },
          },
          {
            title: t('expiry'),
            render: (_: unknown, record: Invitation): ReactNode => {
              return formatDateTime(record.expires_at);
            },
          },
          {
            title: t('action'),
            render: (_: unknown, record: Invitation): ReactNode => {
              return (
                <Space>
                  <Button
                    danger
                    size="small"
                    onClick={(): void => {
                      handleClickDeleteInvitation(record);
                    }}
                  >
                    {t('actions.cancel')}
                  </Button>
                </Space>
              );
            },
          },
        ]}
      />
    </Spin>
  );
};

export { InviteListTable };
